import { useEffect, useMemo, useState } from 'react'
import AppShell from '@/components/AppShell'
import Skeleton from '@/components/Skeleton'
import { useNotifications } from '@/lib/notifications'

type Mill = {
  id: number
  name: string
  email: string
  phone: string
  state: string
  county: string
  dailyQuota?: string
  weeklyQuota?: string
}

const LSK = {
  mine: 'mills:mine:v1',
} as const

const loadJSON = <T,>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k)
    return v ? (JSON.parse(v) as T) : d
  } catch {
    return d
  }
}
const saveJSON = (k: string, v: any) => localStorage.setItem(k, JSON.stringify(v))

export default function MillsIndex() {
  const { push } = useNotifications()
  const [tab, setTab] = useState<'My Mills' | 'Mill Database'>('My Mills')
  const [db, setDb] = useState<Mill[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [qMine, setQMine] = useState('')
  const [qDb, setQDb] = useState('')
  const [mine, setMine] = useState<Mill[]>(loadJSON<Mill[]>(LSK.mine, []))

  useEffect(() => {
    saveJSON(LSK.mine, mine)
  }, [mine])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const res = await fetch('/mills-db.json', { cache: 'no-store' })
        const data: Mill[] = await res.json()
        if (!alive) return
        setDb(data)
      } catch (e) {
        console.error(e)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const filteredMine = useMemo(() => {
    if (!qMine.trim()) return mine
    const s = qMine.toLowerCase()
    return mine.filter((m) =>
      [m.name, m.email, m.phone, m.state, m.county].join(' ').toLowerCase().includes(s),
    )
  }, [mine, qMine])

  const filteredDb = useMemo(() => {
    if (!db) return []
    if (!qDb.trim()) return db
    const s = qDb.toLowerCase()
    return db.filter((m) =>
      [m.name, m.email, m.phone, m.state, m.county].join(' ').toLowerCase().includes(s),
    )
  }, [db, qDb])

  function saveQuota(id: number, field: 'dailyQuota' | 'weeklyQuota', value: string) {
    setMine((list) =>
      list.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    )
    push({ title: 'Saved', body: `${field === 'dailyQuota' ? 'Daily' : 'Weekly'} quota updated` })
  }

  function removeMine(id: number) {
    setMine((list) => list.filter((m) => m.id !== id))
    push({ title: 'Removed mill from My Mills' })
  }

  function addToMine(m: Mill) {
    setMine((list) => {
      if (list.some((x) => x.id === m.id)) return list
      return [...list, { ...m }]
    })
    push({ title: 'Added to My Mills', body: m.name })
  }

  const Tabs = (
    <div className="flex gap-6 mb-4">
      {(['My Mills', 'Mill Database'] as const).map((t) => (
        <button
          key={t}
          className={`pb-2 border-b-2 -mb-px ${
            tab === t ? 'border-[var(--brand)] text-[var(--brand)]' : 'border-transparent text-[var(--muted)]'
          }`}
          onClick={() => setTab(t)}
        >
          {t}
        </button>
      ))}
    </div>
  )

  return (
    <AppShell title="My Mills">
      {Tabs}

      {tab === 'My Mills' && (
        <div className="card">
          <div className="card-header">My Mills</div>
          <div className="card-body">
            <div className="mb-3">
              <input
                className="input w-[420px]"
                placeholder="Search"
                value={qMine}
                onChange={(e) => setQMine(e.target.value)}
              />
            </div>

            <div className="overflow-auto rounded-xl border border-[color:var(--border)]">
              <table className="min-w-[1000px] w-full text-sm">
                <thead className="bg-[color:var(--surface-1)]">
                  <tr className="text-left">
                    <th className="px-3 py-2 w-16">ID</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">State</th>
                    <th className="px-3 py-2">County</th>
                    <th className="px-3 py-2 w-52">Daily Quota</th>
                    <th className="px-3 py-2 w-52">Weekly Quota</th>
                    <th className="px-3 py-2 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMine.length === 0 ? (
                    <tr>
                      <td className="px-3 py-8 text-center text-[var(--muted)]" colSpan={9}>
                        No mills in your list. Go to <strong>Mill Database</strong> to add some.
                      </td>
                    </tr>
                  ) : (
                    filteredMine.map((m) => (
                      <tr key={m.id} className="border-t border-[color:var(--border)]">
                        <td className="px-3 py-2">{m.id}</td>
                        <td className="px-3 py-2">
                          <div className="text-[color:var(--ink-strong)]">{m.name}</div>
                        </td>
                        <td className="px-3 py-2">{m.email || '-'}</td>
                        <td className="px-3 py-2">{m.phone || '-'}</td>
                        <td className="px-3 py-2">{m.state}</td>
                        <td className="px-3 py-2">{m.county}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <input
                              className="input"
                              placeholder="Daily Quota"
                              value={m.dailyQuota || ''}
                              onChange={(e) => saveQuota(m.id, 'dailyQuota', e.target.value)}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <input
                              className="input"
                              placeholder="Weekly Quota"
                              value={m.weeklyQuota || ''}
                              onChange={(e) => saveQuota(m.id, 'weeklyQuota', e.target.value)}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <button className="btn-secondary" onClick={() => removeMine(m.id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-sm text-[var(--muted)]">
              Need to add more mills to this list? Go to the <button className="underline" onClick={()=>setTab('Mill Database')}>Mill Database</button>.
            </div>
          </div>
        </div>
      )}

      {tab === 'Mill Database' && (
        <div className="card">
          <div className="card-header">Mill Database</div>
          <div className="card-body">
            <div className="mb-3">
              <input
                className="input w-[420px]"
                placeholder="Search"
                value={qDb}
                onChange={(e) => setQDb(e.target.value)}
              />
            </div>

            {loading || !db ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="overflow-auto rounded-xl border border-[color:var(--border)]">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-[color:var(--surface-1)]">
                    <tr className="text-left">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Phone</th>
                      <th className="px-3 py-2">State</th>
                      <th className="px-3 py-2">County</th>
                      <th className="px-3 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDb.length === 0 ? (
                      <tr>
                        <td className="px-3 py-8 text-center text-[var(--muted)]" colSpan={6}>
                          No results
                        </td>
                      </tr>
                    ) : (
                      filteredDb.map((m) => {
                        const inMine = mine.some((x) => x.id === m.id)
                        return (
                          <tr key={m.id} className="border-t border-[color:var(--border)]">
                            <td className="px-3 py-2">{m.name}</td>
                            <td className="px-3 py-2">{m.email || '-'}</td>
                            <td className="px-3 py-2">{m.phone || '-'}</td>
                            <td className="px-3 py-2">{m.state}</td>
                            <td className="px-3 py-2">{m.county}</td>
                            <td className="px-3 py-2">
                              {inMine ? (
                                <button className="btn-secondary" onClick={() => removeMine(m.id)}>
                                  Remove
                                </button>
                              ) : (
                                <button className="btn" onClick={() => addToMine(m)}>
                                  Add
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  )
}
